/**
 * MD-25 SEO Bootstrap Dashboard - F-A-40.
 *
 * Route: /dashboard/seo
 * Auth: reuse DashboardGate (tier='admin').
 * Layout: reuse DashboardLayout sidebar.
 *
 * 5 tab Phase 1 (read-only, KHONG D1 write):
 *  1. Cron + IndexNow: 4 cron R2 log 48h, success rate per endpoint
 *  2. GSC Queue: gsc-queue.json + deep link Request Indexing GSC UI
 *  3. Sitemap: cron metadata + audit artifact link
 *  4. Schema Audit: schema marker coverage (build-time helper /seo-audit.json)
 *  5. Internal Link: adjacency + orphan list + suggest inbound
 *
 * Spec: specs/domains/admin/F-A-SEO-DASHBOARD.md
 * Decision: DECISIONS s2026-05-12-SEO-DASHBOARD-MD-25-PHASE-1-LOCK
 */

import { useEffect, useState } from 'react';
import { DashboardGate } from './DashboardGate';
import { DashboardLayout } from './DashboardSidebar';
import { useAdminSeoApi, useSeoAuditArtifact } from './use-admin-seo';

type TabId = 'cron' | 'gsc' | 'sitemap' | 'schema' | 'link' | 'perf' | 'suggest' | 'traffic' | 'daily-brief';

interface TabDef {
  id: TabId;
  label: string;
  sub: string;
}

const TABS: TabDef[] = [
  { id: 'cron', label: 'Cron va IndexNow', sub: 'Theo doi 4 cron SEO va FB cong dong 48 gio gan day' },
  { id: 'gsc', label: 'Hang doi GSC', sub: 'URL chua duoc Google index, founder bam Request Indexing thu cong' },
  { id: 'perf', label: 'Hiệu suất tìm kiếm', sub: 'GSC Search Analytics 90 ngày - từ khóa và trang có lượt hiển thị' },
  { id: 'daily-brief', label: 'Bản tin hằng ngày', sub: 'Báo cáo SEO tổng hợp MD-25 Phase 3 - 4 phần A/B/C/D, autofix log, hàng đợi thủ công' },
  { id: 'traffic', label: 'Lưu lượng truy cập', sub: 'Google Analytics 4 - phiên và trang xem 28 ngày gần đây' },
  { id: 'sitemap', label: 'Sitemap', sub: 'Sitemap chính và sitemap tin tức, ngày cập nhật' },
  { id: 'schema', label: 'Kiem tra schema', sub: 'Article, BlogPosting, FAQ, BreadcrumbList cho 36 bai' },
  { id: 'link', label: 'Lien ket noi bo', sub: 'So lien ket vao va lien ket ra, danh sach bai co don' },
  { id: 'suggest', label: 'Goi y lien ket', sub: 'Goi y noi bai chua co backlink den bai khac, founder duyet thu cong' },
];

// ---------- Types ----------

interface IndexNowEndpoint {
  id: string;
  label: string;
  line_count_48h: number;
  ok_count: number;
  fail_count: number;
  success_rate_pct: number | null;
  last_ts: string | null;
  last_line: string | null;
}

interface IndexNowResp {
  ok: boolean;
  error?: string;
  window?: string;
  endpoints?: IndexNowEndpoint[];
  generated_at?: string;
}

interface GscQueueEntry {
  url: string;
  priority_score?: number;
  priority_reason?: string;
  age_days?: number | null;
  state?: string;
  priority?: string;
}

interface GscQueueResp {
  ok: boolean;
  error?: string;
  queue: {
    generated_at?: string;
    total?: number;
    indexed?: number;
    unindexed_count?: number;
    unindexed?: Array<GscQueueEntry> | string[];
    founder_action?: string;
  } | null;
  note?: string;
}

interface SitemapSubmitLog {
  ts: string;
  feedpath: string;
  http_status: number;
  ok: boolean;
  error?: string;
  trigger?: string;
}

interface SitemapSubmitStatusResp {
  ok: boolean;
  error?: string;
  window?: string;
  history?: SitemapSubmitLog[];
  last_submit_per_feedpath?: Record<string, SitemapSubmitLog>;
  generated_at?: string;
}

interface SitemapResp {
  ok: boolean;
  sitemaps?: Array<{
    id: string;
    path: string;
    source: string;
    last_committed_marker?: string;
    last_cron_run?: string | null;
  }>;
  audit_artifact_url?: string;
  note?: string;
}

interface SeoAuditArticle {
  slug: string;
  cluster: string;
  lang: string;
  title: string;
  published_at: string | null;
  outbound_count: number;
  inbound_count: number;
  orphan: boolean;
  suggest_inbound_from: string[];
  schema: {
    article: boolean;
    blog_posting: boolean;
    faq_page: boolean;
    breadcrumb_list: boolean;
  };
  schema_pass: boolean;
}

interface SeoAuditArtifact {
  schema_version: number;
  generated_at: string;
  summary: {
    total_articles: number;
    schema_pass: number;
    schema_fail: number;
    schema_coverage_pct: number;
    orphan_count: number;
    orphan_ratio_pct: number;
    total_internal_links: number;
  };
  sitemap_diff: {
    sitemap_main_total: number;
    sitemap_main_blog_urls: number;
    blog_manifest_count: number;
    diff: number;
    file_missing: boolean;
  };
  articles: SeoAuditArticle[];
}

// ---------- GSC Performance types (Ship A 2026-05-12) ----------

interface GscTotals {
  impressions: number;
  clicks: number;
  ctr: number;
  avg_position: number;
}

interface GscQueryItem {
  query: string;
  impressions: number;
  clicks: number;
  ctr: number;
  position: number;
  page2_flag: boolean;
}

interface GscPageItem {
  page: string;
  slug: string;
  impressions: number;
  clicks: number;
  ctr: number;
  position: number;
}

interface GscPerfResp {
  ok: boolean;
  mode?: string;
  error?: string;
  site_url?: string;
  window?: { start: string; end: string; days: number };
  previous_window?: { start: string; end: string; days: number };
  totals?: GscTotals;
  previous_totals?: GscTotals;
  top_queries?: GscQueryItem[];
  top_pages?: GscPageItem[];
  generated_at?: string;
}

// ---------- Internal-link suggestion types (Ship A 2026-05-12) ----------

interface SuggestItem {
  target_slug: string;
  target_title: string;
  target_cluster: string;
  anchor_text: string;
  method: 'tfidf' | 'anchor' | 'sibling';
  confidence: number;
  reason: string;
}

interface SuggestArtifact {
  schema_version: number;
  generated_at: string;
  config: { max_suggest_per_article: number; min_confidence: number };
  summary: {
    total_articles: number;
    articles_with_suggestions: number;
    total_suggestions: number;
    by_method: Record<string, number>;
  };
  suggestions_by_slug: Record<string, SuggestItem[]>;
}

// ---------- Helpers ----------

function pctBadgeClass(pct: number | null, warn = 90, fail = 70): string {
  if (pct === null) return 'seo-badge seo-badge-idle';
  if (pct >= warn) return 'seo-badge seo-badge-ok';
  if (pct >= fail) return 'seo-badge seo-badge-warn';
  return 'seo-badge seo-badge-fail';
}

function fmtTs(ts: string | null | undefined): string {
  if (!ts) return 'Chua co';
  try {
    return new Date(ts).toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' });
  } catch {
    return ts;
  }
}

function ageDays(lastmod: string | null | undefined): number | null {
  if (!lastmod) return null;
  const t = new Date(lastmod).getTime();
  if (Number.isNaN(t)) return null;
  return Math.floor((Date.now() - t) / (24 * 3600 * 1000));
}

// ---------- Tab: Cron + IndexNow ----------

function TabCron() {
  const { data, loading, error, refetch } = useAdminSeoApi<IndexNowResp>('/indexnow');
  if (loading) return <p className="seo-empty">Dang tai du lieu cron 48 gio gan day...</p>;
  if (error) return <ErrorBlock error={error} onRetry={refetch} />;
  if (!data || !data.ok) return <ErrorBlock error={data?.error ?? 'Khong co du lieu'} onRetry={refetch} />;
  const endpoints = data.endpoints ?? [];
  return (
    <div className="seo-tab-body">
      <div className="seo-tab-head">
        <p className="seo-tab-meta">
          Cua so: {data.window ?? '48 gio'} | Cập nhật luc {fmtTs(data.generated_at)}
        </p>
        <button type="button" className="seo-refresh-btn" onClick={() => void refetch()}>
          Lam moi du lieu
        </button>
      </div>
      <table className="seo-table">
        <thead>
          <tr>
            <th>Cron</th>
            <th>Mo ta</th>
            <th>So dong log</th>
            <th>Thanh cong</th>
            <th>That bai</th>
            <th>Ti le %</th>
            <th>Lan chay cuoi</th>
          </tr>
        </thead>
        <tbody>
          {endpoints.map((e) => (
            <tr key={e.id}>
              <td><code>{e.id}</code></td>
              <td>{e.label}</td>
              <td>{e.line_count_48h}</td>
              <td>{e.ok_count}</td>
              <td>{e.fail_count}</td>
              <td>
                <span className={pctBadgeClass(e.success_rate_pct)}>
                  {e.success_rate_pct === null ? 'Chua co' : `${e.success_rate_pct}%`}
                </span>
              </td>
              <td>{fmtTs(e.last_ts)}</td>
            </tr>
          ))}
          {endpoints.length === 0 ? (
            <tr><td colSpan={7} className="seo-empty">Chua co du lieu trong cua so 48 gio.</td></tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

// ---------- Tab: GSC Queue ----------

// ICT date YYYY-MM-DD (UTC+7) for per-day localStorage key
function ictDateKey(): string {
  const d = new Date(Date.now() + 7 * 3600 * 1000);
  return d.toISOString().slice(0, 10);
}

// Hour-of-day in ICT (UTC+7)
function ictHour(): number {
  return new Date(Date.now() + 7 * 3600 * 1000).getUTCHours();
}

function priorityBadgeClass(score: number): string {
  if (score >= 80) return 'seo-priority-badge seo-priority-high';
  if (score >= 50) return 'seo-priority-badge seo-priority-mid';
  return 'seo-priority-badge seo-priority-low';
}

function priorityLabel(score: number): string {
  if (score >= 80) return 'Cao';
  if (score >= 50) return 'Trung binh';
  return 'Thap';
}

const GSC_REQUESTED_KEY_PREFIX = 'seo:gsc:requested:';
const GSC_DAILY_CAP = 10;

function loadRequestedToday(): Record<string, number> {
  try {
    const key = `${GSC_REQUESTED_KEY_PREFIX}${ictDateKey()}:v1`;
    const raw = localStorage.getItem(key);
    if (!raw) return {};
    return JSON.parse(raw) as Record<string, number>;
  } catch {
    return {};
  }
}

function saveRequestedToday(map: Record<string, number>): void {
  try {
    const key = `${GSC_REQUESTED_KEY_PREFIX}${ictDateKey()}:v1`;
    localStorage.setItem(key, JSON.stringify(map));
  } catch {
    // ignore quota
  }
}

function buildGscInspectUrl(url: string): string {
  return `https://search.google.com/search-console/inspect?resource_id=sc-domain%3Aasklaw.vn&id=${encodeURIComponent(url)}`;
}

// ---------- Manual queue (founder paste URL) -----------
// Per founder feedback 2026-05-12: paste URL tay vao queue, KHONG cho cron.

const MANUAL_QUEUE_KEY = 'seo:gsc:manual_queue:v1';
const MANUAL_QUEUE_MAX = 100;
const URL_PATTERN = /^https?:\/\/[^\s/$.?#].[^\s]*$/i;

interface ManualQueueEntry {
  url: string;
  added_at: number;
  priority_score: number;
}

function loadManualQueue(): ManualQueueEntry[] {
  try {
    const raw = localStorage.getItem(MANUAL_QUEUE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw) as unknown;
    if (!Array.isArray(arr)) return [];
    return arr.filter((it): it is ManualQueueEntry =>
      it && typeof it === 'object' &&
      typeof (it as ManualQueueEntry).url === 'string' &&
      typeof (it as ManualQueueEntry).added_at === 'number',
    ).map((it) => ({
      url: it.url,
      added_at: it.added_at,
      priority_score: typeof it.priority_score === 'number' ? it.priority_score : 50,
    }));
  } catch {
    return [];
  }
}

function saveManualQueue(list: ManualQueueEntry[]): void {
  try {
    localStorage.setItem(MANUAL_QUEUE_KEY, JSON.stringify(list));
  } catch {
    // ignore quota
  }
}

function parseManualUrlInput(raw: string): { valid: string[]; invalid: string[] } {
  const lines = raw.split('\n').map((s) => s.trim()).filter((s) => s.length > 0);
  const valid: string[] = [];
  const invalid: string[] = [];
  for (const ln of lines) {
    if (URL_PATTERN.test(ln)) valid.push(ln);
    else invalid.push(ln);
  }
  return { valid, invalid };
}

async function triggerGscCheckCron(): Promise<{ ok: boolean; message: string }> {
  try {
    const token = (() => {
      try { return localStorage.getItem('asklaw_access_token') ?? ''; } catch { return ''; }
    })();
    const res = await fetch('/api/v1/admin/seo/cron/run-gsc-check', {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const json = (await res.json()) as { ok: boolean; error?: string; trigger_id?: string; estimate_complete_s?: number };
    if (!json.ok) {
      return { ok: false, message: `Loi: ${json.error ?? 'unknown'}` };
    }
    return {
      ok: true,
      message: `Trigger thanh cong (id: ${json.trigger_id ?? '-'}). Hoan thanh sau ~${json.estimate_complete_s ?? 60}s. Bam "Lam moi du lieu" sau do.`,
    };
  } catch (e) {
    return { ok: false, message: `Loi: ${(e as Error).message}` };
  }
}

function TabGsc() {
  const { data, loading, error, refetch } = useAdminSeoApi<GscQueueResp>('/gsc-queue');
  const [requested, setRequested] = useState<Record<string, number>>(() => loadRequestedToday());
  const [showAll, setShowAll] = useState<boolean>(false);
  const [manualQueue, setManualQueue] = useState<ManualQueueEntry[]>(() => loadManualQueue());
  const [manualInput, setManualInput] = useState<string>('');
  const [manualMsg, setManualMsg] = useState<string | null>(null);
  const [triggering, setTriggering] = useState<boolean>(false);
  const [triggerMsg, setTriggerMsg] = useState<string | null>(null);

  // Reload from localStorage on mount to refresh on date change (midnight ICT).
  useEffect(() => {
    setRequested(loadRequestedToday());
    setManualQueue(loadManualQueue());
  }, []);

  if (loading) return <p className="seo-empty">Dang tai hang doi GSC...</p>;
  if (error) return <ErrorBlock error={error} onRetry={refetch} />;
  if (!data || !data.ok) return <ErrorBlock error={data?.error ?? 'Khong co du lieu'} onRetry={refetch} />;
  const q = data.queue;
  const cronList: GscQueueEntry[] = q && Array.isArray(q.unindexed)
    ? q.unindexed.map((it) =>
        typeof it === 'string'
          ? { url: it, priority_score: 50, priority_reason: 'baseline' }
          : { ...it, priority_score: it.priority_score ?? 50, priority_reason: it.priority_reason ?? 'baseline' },
      )
    : [];

  // Combine manual + cron. Manual priority first (per founder spec), dedupe by URL.
  const manualUrls = new Set(manualQueue.map((m) => m.url));
  const combined: Array<GscQueueEntry & { source: 'manual' | 'cron'; added_at?: number }> = [
    ...manualQueue.map((m) => ({
      url: m.url,
      priority_score: m.priority_score,
      priority_reason: 'manual_input',
      source: 'manual' as const,
      added_at: m.added_at,
    })),
    ...cronList
      .filter((c) => !manualUrls.has(c.url))
      .map((c) => ({ ...c, source: 'cron' as const })),
  ];
  // Sort: manual first (uu tien), then by priority_score DESC, then added_at DESC.
  combined.sort((a, b) => {
    if (a.source !== b.source) return a.source === 'manual' ? -1 : 1;
    const ps = (b.priority_score ?? 0) - (a.priority_score ?? 0);
    if (ps !== 0) return ps;
    return (b.added_at ?? 0) - (a.added_at ?? 0);
  });

  const requestedToday = Object.keys(requested).length;
  const remaining = Math.max(0, GSC_DAILY_CAP - requestedToday);
  const visible = showAll ? combined : combined.filter((r) => !requested[r.url]);

  function markRequested(url: string): void {
    setRequested((s) => {
      const next = { ...s, [url]: Date.now() };
      saveRequestedToday(next);
      return next;
    });
  }

  function unmarkRequested(url: string): void {
    setRequested((s) => {
      const next = { ...s };
      delete next[url];
      saveRequestedToday(next);
      return next;
    });
  }

  function openInspect(url: string): void {
    window.open(buildGscInspectUrl(url), '_blank', 'noopener,noreferrer');
  }

  function handleAddManual(): void {
    setManualMsg(null);
    const parsed = parseManualUrlInput(manualInput);
    if (parsed.valid.length === 0 && parsed.invalid.length === 0) {
      setManualMsg('Chua co URL nao.');
      return;
    }
    if (parsed.invalid.length > 0 && parsed.valid.length === 0) {
      setManualMsg(`Tất cả ${parsed.invalid.length} dòng không đúng định dạng URL (phải bắt đầu https:// hoặc http://).`);
      return;
    }
    const existing = new Set(manualQueue.map((m) => m.url));
    const now = Date.now();
    const toAdd: ManualQueueEntry[] = [];
    let dupCount = 0;
    for (const u of parsed.valid) {
      if (existing.has(u)) {
        dupCount++;
        continue;
      }
      existing.add(u);
      toAdd.push({ url: u, added_at: now, priority_score: 50 });
    }
    const merged = [...manualQueue, ...toAdd];
    if (merged.length > MANUAL_QUEUE_MAX) {
      setManualMsg(`Vuot ${MANUAL_QUEUE_MAX} URL toi da. Xoa bot URL cu truoc khi them.`);
      return;
    }
    setManualQueue(merged);
    saveManualQueue(merged);
    setManualInput('');
    const parts: string[] = [];
    if (toAdd.length > 0) parts.push(`Da them ${toAdd.length} URL`);
    if (dupCount > 0) parts.push(`bo qua ${dupCount} URL trung`);
    if (parsed.invalid.length > 0) parts.push(`bo qua ${parsed.invalid.length} dong sai dinh dang`);
    setManualMsg(parts.join(' | '));
  }

  function handleRemoveManual(url: string): void {
    const next = manualQueue.filter((m) => m.url !== url);
    setManualQueue(next);
    saveManualQueue(next);
  }

  function bulkOpenTen(): void {
    const candidates = combined.filter((r) => !requested[r.url]).slice(0, GSC_DAILY_CAP);
    if (candidates.length === 0) {
      alert('Khong con URL nao chua request hom nay.');
      return;
    }
    const manualCount = candidates.filter((c) => c.source === 'manual').length;
    const cronCount = candidates.length - manualCount;
    const proceed = window.confirm(
      `Sẽ mở ${candidates.length} tab GSC (${manualCount} tay + ${cronCount} auto) và đánh dấu "Đã request". Tiếp tục?`,
    );
    if (!proceed) return;
    const nextMap: Record<string, number> = { ...requested };
    const now = Date.now();
    for (const c of candidates) {
      window.open(buildGscInspectUrl(c.url), '_blank', 'noopener,noreferrer');
      nextMap[c.url] = now;
    }
    setRequested(nextMap);
    saveRequestedToday(nextMap);
  }

  async function handleTriggerCron(): Promise<void> {
    setTriggering(true);
    setTriggerMsg(null);
    const r = await triggerGscCheckCron();
    setTriggerMsg(r.message);
    setTriggering(false);
  }

  const showReminder = ictHour() >= 12 && remaining > 0 && combined.length > 0;

  return (
    <div className="seo-tab-body">
      {showReminder ? (
        <div className="seo-reminder-banner">
          Sau 12:00 ICT - con <strong>{remaining}</strong> URL chua request hom nay (Google cap {GSC_DAILY_CAP}/ngay).
          Bam "Mo {Math.min(remaining, GSC_DAILY_CAP)} tab GSC" de xu ly nhanh.
        </div>
      ) : null}

      {/* Manual URL input - founder paste URL tay vao queue */}
      <div className="seo-manual-input-block" style={{ marginBottom: '1rem', padding: '0.75rem', border: '1px solid #d4d4d8', borderRadius: '6px', background: '#fafafa' }}>
        <p style={{ margin: '0 0 0.5rem 0', fontWeight: 600 }}>Them URL tay vao hang doi</p>
        <textarea
          value={manualInput}
          onChange={(e) => setManualInput(e.target.value)}
          placeholder="Moi dong 1 URL. Vi du: https://asklaw.vn/blog/hop-dong-thu-viec-quy-dinh"
          rows={4}
          style={{ width: '100%', fontFamily: 'monospace', fontSize: '0.85rem', padding: '0.5rem', border: '1px solid #d4d4d8', borderRadius: '4px' }}
        />
        <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <button type="button" className="seo-refresh-btn" onClick={handleAddManual}>
            Them vao hang doi
          </button>
          <span style={{ fontSize: '0.85rem', color: '#52525b' }}>
            Manual queue: <strong>{manualQueue.length}</strong>/{MANUAL_QUEUE_MAX}
          </span>
          {manualMsg ? <span style={{ fontSize: '0.85rem', color: '#16a34a' }}>{manualMsg}</span> : null}
        </div>
      </div>

      <div className="seo-tab-head">
        <p className="seo-tab-meta">
          Cron: tong {q?.total ?? 0} URL | Da index {q?.indexed ?? 0} | Chua index {q?.unindexed_count ?? cronList.length}
          {' | Tay '}<strong>{manualQueue.length}</strong>
          {' | Da request hom nay '}
          <strong>{requestedToday}</strong>/{GSC_DAILY_CAP}
        </p>
        {!q ? (
          <p className="seo-note" style={{ color: '#a16207' }}>
            {data.note ?? 'gsc-queue.json chua ton tai - cron seo-gsc-check chua chay'}
          </p>
        ) : null}
        <div className="seo-bulk-action-bar">
          <button
            type="button"
            className="seo-refresh-btn seo-bulk-btn"
            onClick={bulkOpenTen}
            disabled={remaining === 0}
            title={remaining === 0 ? 'Da du 10/10 hom nay' : `Mo ${Math.min(remaining, GSC_DAILY_CAP)} tab GSC`}
          >
            Mo {Math.min(remaining, GSC_DAILY_CAP)} tab GSC
          </button>
          <button
            type="button"
            className="seo-refresh-btn"
            onClick={() => void handleTriggerCron()}
            disabled={triggering}
            title="Chay cron kiem tra GSC ngay (khong cho 08:00 ICT mai)"
          >
            {triggering ? 'Dang chay...' : 'Chay cron kiem tra GSC ngay'}
          </button>
          <label className="seo-checkbox-label">
            <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
            Hien ca URL da request
          </label>
          <button type="button" className="seo-refresh-btn" onClick={() => void refetch()}>
            Lam moi du lieu
          </button>
        </div>
        {triggerMsg ? (
          <p className="seo-note" style={{ marginTop: '0.5rem' }}>{triggerMsg}</p>
        ) : null}
      </div>
      <table className="seo-table">
        <thead>
          <tr>
            <th>Nguon</th>
            <th>Uu tien</th>
            <th>Duong dan</th>
            <th>Ly do</th>
            <th>Tuoi (ngay)</th>
            <th>Hanh dong</th>
          </tr>
        </thead>
        <tbody>
          {visible.slice(0, 100).map((row) => {
            const isRequested = Boolean(requested[row.url]);
            const score = row.priority_score ?? 50;
            return (
              <tr key={row.url} className={isRequested ? 'seo-row-ok' : ''}>
                <td>
                  <span
                    style={{
                      display: 'inline-block',
                      padding: '0.1rem 0.4rem',
                      fontSize: '0.75rem',
                      borderRadius: '3px',
                      background: row.source === 'manual' ? '#dbeafe' : '#f3f4f6',
                      color: row.source === 'manual' ? '#1e40af' : '#52525b',
                      fontWeight: 600,
                    }}
                  >
                    {row.source === 'manual' ? 'Tay' : 'Auto'}
                  </span>
                </td>
                <td>
                  <span className={priorityBadgeClass(score)} title={`Score ${score}`}>
                    {priorityLabel(score)} ({score})
                  </span>
                </td>
                <td><a href={row.url} target="_blank" rel="noreferrer">{row.url}</a></td>
                <td><small><code>{row.priority_reason ?? '-'}</code></small></td>
                <td>{row.age_days === null || row.age_days === undefined ? '-' : `${row.age_days}`}</td>
                <td>
                  <button type="button" className="seo-action-link" onClick={() => openInspect(row.url)}>
                    Mo Inspect
                  </button>
                  {' | '}
                  {isRequested ? (
                    <button type="button" className="seo-action-link" onClick={() => unmarkRequested(row.url)}>
                      Huy danh dau
                    </button>
                  ) : (
                    <button type="button" className="seo-action-link" onClick={() => markRequested(row.url)}>
                      Da request
                    </button>
                  )}
                  {row.source === 'manual' ? (
                    <>
                      {' | '}
                      <button
                        type="button"
                        className="seo-action-link"
                        onClick={() => handleRemoveManual(row.url)}
                        style={{ color: '#dc2626' }}
                      >
                        Xoa
                      </button>
                    </>
                  ) : null}
                </td>
              </tr>
            );
          })}
          {visible.length === 0 ? (
            <tr><td colSpan={6} className="seo-empty">
              {combined.length === 0
                ? 'Hang doi rong. Paste URL vao o tren hoac bam "Chay cron kiem tra GSC ngay".'
                : 'Da request tat ca URL hien thi. Bat "Hien ca URL da request" de xem lai.'}
            </td></tr>
          ) : null}
        </tbody>
      </table>
      {q?.founder_action ? (
        <p className="seo-note">Goi y cho founder: {q.founder_action}</p>
      ) : null}
    </div>
  );
}

// ---------- Tab: Sitemap ----------

function TabSitemap() {
  const { data, loading, error, refetch } = useAdminSeoApi<SitemapResp>('/sitemap');
  const submitStatus = useAdminSeoApi<SitemapSubmitStatusResp>('/sitemap/submit-status');
  const audit = useSeoAuditArtifact<SeoAuditArtifact>('/seo-audit.json');
  const [showStaleOnly, setShowStaleOnly] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState<string | null>(null);

  async function handleManualSubmit(): Promise<void> {
    setSubmitting(true);
    setSubmitMsg(null);
    try {
      const token = (() => {
        try { return localStorage.getItem('asklaw_access_token') ?? ''; } catch { return ''; }
      })();
      const res = await fetch('/api/v1/admin/seo/sitemap/submit', {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const json = (await res.json()) as { ok: boolean; error?: string; mode?: string; results?: Array<{ feedpath: string; http_status: number; ok: boolean; error?: string }> };
      if (!json.ok) {
        setSubmitMsg(`Loi: ${json.error ?? json.mode ?? 'unknown'}`);
      } else {
        const okCount = (json.results ?? []).filter((r) => r.ok).length;
        const total = (json.results ?? []).length;
        setSubmitMsg(`Submit thanh cong ${okCount}/${total} sitemap.`);
        void submitStatus.refetch();
      }
    } catch (e) {
      setSubmitMsg(`Loi: ${(e as Error).message}`);
    } finally {
      setSubmitting(false);
    }
  }
  if (loading) return <p className="seo-empty">Dang tai sitemap...</p>;
  if (error) return <ErrorBlock error={error} onRetry={refetch} />;
  if (!data || !data.ok) return <ErrorBlock error={'Khong co du lieu sitemap'} onRetry={refetch} />;
  const sitemaps = data.sitemaps ?? [];
  const diff = audit.data?.sitemap_diff;
  return (
    <div className="seo-tab-body">
      <div className="seo-tab-head">
        <p className="seo-tab-meta">{data.note ?? ''}</p>
        <button type="button" className="seo-refresh-btn" onClick={() => { void refetch(); void audit.refetch(); }}>
          Lam moi du lieu
        </button>
      </div>
      <table className="seo-table">
        <thead>
          <tr>
            <th>Sitemap</th>
            <th>Duong dan</th>
            <th>Nguon</th>
            <th>Lan chay cuoi</th>
          </tr>
        </thead>
        <tbody>
          {sitemaps.map((s) => (
            <tr key={s.id}>
              <td><code>{s.id}</code></td>
              <td><a href={s.path} target="_blank" rel="noreferrer">{s.path}</a></td>
              <td>{s.source}</td>
              <td>{s.last_committed_marker ?? fmtTs(s.last_cron_run)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="seo-card">
        <h3 className="seo-card-title">Lich su submit GSC Sitemaps API</h3>
        <p className="seo-note">
          GSC Webmasters API v3 (PUT /sitemaps/{`{feedpath}`}). Cron `seo-gsc-check` 08:00 ICT auto-submit
          sau khi sinh hang doi. Founder co the trigger manual ben duoi.
        </p>
        <div className="seo-bulk-action-bar">
          <button
            type="button"
            className="seo-refresh-btn"
            onClick={() => void handleManualSubmit()}
            disabled={submitting}
          >
            {submitting ? 'Dang submit...' : 'Submit lai 2 sitemap ngay'}
          </button>
          <button
            type="button"
            className="seo-refresh-btn"
            onClick={() => void submitStatus.refetch()}
          >
            Lam moi lich su
          </button>
          {submitMsg ? <span className="seo-note">{submitMsg}</span> : null}
        </div>
        {submitStatus.loading ? (
          <p className="seo-empty">Dang tai lich su submit...</p>
        ) : submitStatus.data?.ok ? (
          <table className="seo-table">
            <thead>
              <tr>
                <th>Sitemap</th>
                <th>Lan submit cuoi (UTC)</th>
                <th>HTTP status</th>
                <th>Ket qua</th>
                <th>Trigger</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(submitStatus.data.last_submit_per_feedpath ?? {}).length === 0 ? (
                <tr><td colSpan={5} className="seo-empty">Chua co lan submit nao trong 7 ngay gan day.</td></tr>
              ) : (
                Object.entries(submitStatus.data.last_submit_per_feedpath ?? {}).map(([feed, log]) => (
                  <tr key={feed}>
                    <td><code>{feed}</code></td>
                    <td>{fmtTs(log.ts)}</td>
                    <td>{log.http_status || '-'}</td>
                    <td>
                      <span className={log.ok ? 'seo-badge seo-badge-ok' : 'seo-badge seo-badge-fail'}>
                        {log.ok ? 'OK' : `Loi${log.error ? ': ' + log.error.slice(0, 60) : ''}`}
                      </span>
                    </td>
                    <td>{log.trigger ?? '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        ) : (
          <p className="seo-note">{submitStatus.data?.error ?? 'Khong tai duoc lich su submit.'}</p>
        )}
      </div>
      {diff ? (
        <div className="seo-card">
          <h3 className="seo-card-title">Kiem tra dong bo sitemap chinh va blog</h3>
          <p>Sitemap chinh tong: <strong>{diff.sitemap_main_total}</strong> URL</p>
          <p>Trong do URL blog: <strong>{diff.sitemap_main_blog_urls}</strong></p>
          <p>So bai blog trong manifest: <strong>{diff.blog_manifest_count}</strong></p>
          <p>
            Chenh lech: <strong className={diff.diff === 0 ? 'seo-ok' : 'seo-warn'}>{diff.diff}</strong>
            {diff.diff === 0 ? ' (khop)' : diff.diff > 0 ? ' (sitemap thua)' : ' (sitemap thieu)'}
          </p>
          <label className="seo-checkbox-label">
            <input type="checkbox" checked={showStaleOnly} onChange={(e) => setShowStaleOnly(e.target.checked)} />
            Chi hien URL cu hon 30 ngay
          </label>
          <p className="seo-note">
            Chi tiet URL trong file <a href="/sitemap-main.xml" target="_blank" rel="noreferrer">/sitemap-main.xml</a>.
            Du lieu audit: <a href="/seo-audit.json" target="_blank" rel="noreferrer">/seo-audit.json</a>.
          </p>
        </div>
      ) : null}
    </div>
  );
}

// ---------- Tab: Schema Audit ----------

function TabSchema() {
  const { data, loading, error, refetch } = useSeoAuditArtifact<SeoAuditArtifact>('/seo-audit.json');
  if (loading) return <p className="seo-empty">Dang tai du lieu kiem tra schema...</p>;
  if (error) return <ErrorBlock error={error} onRetry={refetch} />;
  if (!data) return <p className="seo-empty">Khong co du lieu.</p>;
  const s = data.summary;
  return (
    <div className="seo-tab-body">
      <div className="seo-tab-head">
        <p className="seo-tab-meta">
          Tong {s.total_articles} bai | Dat <strong>{s.schema_pass}</strong> | Chua dat <strong>{s.schema_fail}</strong>
          {' | Bao phu '}
          <span className={pctBadgeClass(s.schema_coverage_pct, 90, 70)}>{s.schema_coverage_pct}%</span>
          {' | Cập nhật '}{fmtTs(data.generated_at)}
        </p>
        <button type="button" className="seo-refresh-btn" onClick={() => void refetch()}>
          Lam moi du lieu
        </button>
      </div>
      <table className="seo-table">
        <thead>
          <tr>
            <th>Bai blog</th>
            <th>Cluster</th>
            <th>Ngon ngu</th>
            <th>Article</th>
            <th>BlogPosting</th>
            <th>FAQPage</th>
            <th>BreadcrumbList</th>
            <th>Tong ket</th>
          </tr>
        </thead>
        <tbody>
          {data.articles.map((a) => (
            <tr key={a.slug} className={a.schema_pass ? '' : 'seo-row-warn'}>
              <td><a href={`/blog/${a.slug}`} target="_blank" rel="noreferrer">{a.title}</a></td>
              <td><code>{a.cluster}</code></td>
              <td>{a.lang}</td>
              <td>{a.schema.article ? 'Co' : 'Khong'}</td>
              <td>{a.schema.blog_posting ? 'Co' : 'Khong'}</td>
              <td>{a.schema.faq_page ? 'Co' : 'Khong'}</td>
              <td>{a.schema.breadcrumb_list ? 'Co' : 'Khong'}</td>
              <td>
                <span className={a.schema_pass ? 'seo-badge seo-badge-ok' : 'seo-badge seo-badge-warn'}>
                  {a.schema_pass ? 'Dat' : 'Chua dat'}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------- Tab: Internal Link + Orphan ----------

function TabLink() {
  const { data, loading, error, refetch } = useSeoAuditArtifact<SeoAuditArtifact>('/seo-audit.json');
  const [orphanOnly, setOrphanOnly] = useState(false);
  if (loading) return <p className="seo-empty">Dang tai do thi lien ket noi bo...</p>;
  if (error) return <ErrorBlock error={error} onRetry={refetch} />;
  if (!data) return <p className="seo-empty">Khong co du lieu.</p>;
  const s = data.summary;
  const rows = orphanOnly ? data.articles.filter((a) => a.orphan) : data.articles;
  return (
    <div className="seo-tab-body">
      <div className="seo-tab-head">
        <p className="seo-tab-meta">
          Tong {s.total_articles} bai | Co don <strong>{s.orphan_count}</strong> ({s.orphan_ratio_pct}%)
          {' | Tong lien ket noi bo '}<strong>{s.total_internal_links}</strong>
          {' | Cập nhật '}{fmtTs(data.generated_at)}
        </p>
        <label className="seo-checkbox-label">
          <input type="checkbox" checked={orphanOnly} onChange={(e) => setOrphanOnly(e.target.checked)} />
          Chi hien bai co don
        </label>
        <button type="button" className="seo-refresh-btn" onClick={() => void refetch()}>
          Lam moi du lieu
        </button>
      </div>
      <table className="seo-table">
        <thead>
          <tr>
            <th>Bai blog</th>
            <th>Cluster</th>
            <th>Vao</th>
            <th>Ra</th>
            <th>Trang thai</th>
            <th>Goi y them lien ket tu</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((a) => (
            <tr key={a.slug} className={a.orphan ? 'seo-row-warn' : ''}>
              <td><a href={`/blog/${a.slug}`} target="_blank" rel="noreferrer">{a.title}</a></td>
              <td><code>{a.cluster}</code></td>
              <td>{a.inbound_count}</td>
              <td>{a.outbound_count}</td>
              <td>
                <span className={a.orphan ? 'seo-badge seo-badge-warn' : 'seo-badge seo-badge-ok'}>
                  {a.orphan ? 'Co don' : 'Co lien ket vao'}
                </span>
              </td>
              <td>
                {a.suggest_inbound_from.length === 0 ? (
                  <em>Khong can</em>
                ) : (
                  <ul className="seo-suggest-list">
                    {a.suggest_inbound_from.map((s2) => (
                      <li key={s2}><a href={`/blog/${s2}`} target="_blank" rel="noreferrer">/blog/{s2}</a></li>
                    ))}
                  </ul>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------- Tab: GSC Search Analytics (Ship A 2026-05-12) ----------

function fmtPct(v: number): string {
  return `${(v * 100).toFixed(2)}%`;
}

function fmtPos(v: number): string {
  if (!v) return '-';
  return v.toFixed(1);
}

function trendDelta(curr: number, prev: number): { pct: number; up: boolean; same: boolean } {
  if (prev === 0) {
    if (curr === 0) return { pct: 0, up: false, same: true };
    return { pct: 100, up: true, same: false };
  }
  const pct = ((curr - prev) / prev) * 100;
  return { pct: Math.abs(pct), up: pct > 0, same: Math.abs(pct) < 0.5 };
}

function TrendBadge({ curr, prev, invert = false }: { curr: number; prev: number; invert?: boolean }) {
  const t = trendDelta(curr, prev);
  if (t.same) return <span className="seo-trend-same">~ {t.pct.toFixed(1)}%</span>;
  // For avg_position, lower is better -> invert color
  const isGood = invert ? !t.up : t.up;
  const cls = isGood ? 'seo-trend-up' : 'seo-trend-down';
  return <span className={cls}>{t.up ? '+' : '-'}{t.pct.toFixed(1)}%</span>;
}

function TabPerf() {
  const { data, loading, error, refetch } = useAdminSeoApi<GscPerfResp>('/gsc/performance');
  if (loading) return <p className="seo-empty">Dang tai du lieu Google Search Console...</p>;
  if (error) return <ErrorBlock error={error} onRetry={refetch} />;
  if (!data) return <p className="seo-empty">Khong co du lieu.</p>;
  if (!data.ok) {
    if (data.mode === 'no_credentials') {
      return (
        <div className="seo-empty-state">
          <p>GSC credential chua duoc cau hinh tren production. Founder se grant secret roi redeploy.</p>
          <p className="seo-note">Secret can co: <code>GOOGLE_OAUTH_CLIENT_ID</code>, <code>GOOGLE_OAUTH_CLIENT_SECRET</code>, <code>GSC_REFRESH_TOKEN</code>.</p>
        </div>
      );
    }
    if (data.mode === 'gsc_scope_missing') {
      return (
        <div className="seo-empty-state">
          <p>OAuth scope <code>webmasters.readonly</code> chua duoc cap. Founder re-consent tai Google OAuth screen.</p>
        </div>
      );
    }
    if (data.mode === 'gsc_auth_failed') {
      return <ErrorBlock error={data.error ?? 'Xac thuc GSC that bai'} onRetry={refetch} />;
    }
    if (data.mode === 'gsc_rate_limited') {
      return <ErrorBlock error={data.error ?? 'GSC rate limited'} onRetry={refetch} />;
    }
    return <ErrorBlock error={data.error ?? 'Khong tai duoc du lieu GSC'} onRetry={refetch} />;
  }
  const totals = data.totals ?? { impressions: 0, clicks: 0, ctr: 0, avg_position: 0 };
  const prev = data.previous_totals ?? { impressions: 0, clicks: 0, ctr: 0, avg_position: 0 };
  const queries = data.top_queries ?? [];
  const pages = data.top_pages ?? [];
  const isEmpty = totals.impressions === 0;
  return (
    <div className="seo-tab-body">
      <div className="seo-tab-head">
        <p className="seo-tab-meta">
          Site: <code>{data.site_url}</code> | Cua so {data.window?.days ?? 90} ngay ({data.window?.start} den {data.window?.end})
          {' | Cập nhật '}{fmtTs(data.generated_at)}
        </p>
        <button type="button" className="seo-refresh-btn" onClick={() => void refetch()}>
          Lam moi du lieu
        </button>
      </div>
      <div className="seo-totals-grid">
        <div className="seo-total-card">
          <div className="seo-total-label">Luot hien thi</div>
          <div className="seo-total-value">{totals.impressions.toLocaleString('vi-VN')}</div>
          <div className="seo-total-trend"><TrendBadge curr={totals.impressions} prev={prev.impressions} /> so voi 30 ngay truoc do</div>
        </div>
        <div className="seo-total-card">
          <div className="seo-total-label">Luot click</div>
          <div className="seo-total-value">{totals.clicks.toLocaleString('vi-VN')}</div>
          <div className="seo-total-trend"><TrendBadge curr={totals.clicks} prev={prev.clicks} /> so voi 30 ngay truoc do</div>
        </div>
        <div className="seo-total-card">
          <div className="seo-total-label">CTR</div>
          <div className="seo-total-value">{fmtPct(totals.ctr)}</div>
          <div className="seo-total-trend"><TrendBadge curr={totals.ctr} prev={prev.ctr} /> so voi 30 ngay truoc do</div>
        </div>
        <div className="seo-total-card">
          <div className="seo-total-label">Vi tri trung binh</div>
          <div className="seo-total-value">{fmtPos(totals.avg_position)}</div>
          <div className="seo-total-trend"><TrendBadge curr={totals.avg_position} prev={prev.avg_position} invert /> so voi 30 ngay truoc do</div>
        </div>
      </div>
      {isEmpty ? (
        <p className="seo-empty">
          Chua co du lieu impression trong cua so 90 ngay. Property GSC moi can 2-3 ngay de Google bat dau bao cao.
          Quay lai sau khi dat le luot hien thi dau tien.
        </p>
      ) : null}
      <h3 className="seo-card-title">Top 50 tu khoa (sap xep theo luot hien thi)</h3>
      <table className="seo-table">
        <thead>
          <tr>
            <th>Tu khoa</th>
            <th>Luot hien thi</th>
            <th>Click</th>
            <th>CTR</th>
            <th>Vi tri TB</th>
            <th>Trang 2</th>
          </tr>
        </thead>
        <tbody>
          {queries.map((q) => (
            <tr key={q.query || '_empty_'}>
              <td>{q.query || <em>(rong)</em>}</td>
              <td>{q.impressions.toLocaleString('vi-VN')}</td>
              <td>{q.clicks.toLocaleString('vi-VN')}</td>
              <td>{fmtPct(q.ctr)}</td>
              <td>{fmtPos(q.position)}</td>
              <td>{q.page2_flag ? <span className="seo-badge seo-badge-warn">Trang 2</span> : ''}</td>
            </tr>
          ))}
          {queries.length === 0 ? (
            <tr><td colSpan={6} className="seo-empty">Chua co tu khoa.</td></tr>
          ) : null}
        </tbody>
      </table>
      <h3 className="seo-card-title">Top 50 trang (sap xep theo click)</h3>
      <table className="seo-table">
        <thead>
          <tr>
            <th>Trang</th>
            <th>Luot hien thi</th>
            <th>Click</th>
            <th>CTR</th>
            <th>Vi tri TB</th>
          </tr>
        </thead>
        <tbody>
          {pages.slice().sort((a, b) => b.clicks - a.clicks).map((p) => (
            <tr key={p.page}>
              <td><a href={p.page} target="_blank" rel="noreferrer">{p.slug || p.page}</a></td>
              <td>{p.impressions.toLocaleString('vi-VN')}</td>
              <td>{p.clicks.toLocaleString('vi-VN')}</td>
              <td>{fmtPct(p.ctr)}</td>
              <td>{fmtPos(p.position)}</td>
            </tr>
          ))}
          {pages.length === 0 ? (
            <tr><td colSpan={5} className="seo-empty">Chua co trang.</td></tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

// ---------- Tab: Internal Link Suggestions (Ship A 2026-05-12) ----------

const SUGGEST_DECISIONS_KEY = 'seo:link:decisions:v1';

interface SuggestDecisionMap {
  // source_slug -> array of approved target_slug
  [source: string]: string[];
}

function loadSuggestDecisions(): SuggestDecisionMap {
  try {
    const raw = localStorage.getItem(SUGGEST_DECISIONS_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as SuggestDecisionMap;
  } catch {
    return {};
  }
}

function saveSuggestDecisions(map: SuggestDecisionMap): void {
  try {
    localStorage.setItem(SUGGEST_DECISIONS_KEY, JSON.stringify(map));
  } catch {
    // ignore quota
  }
}

function exportApprovedMarkdown(map: SuggestDecisionMap, artifact: SuggestArtifact): string {
  const lines: string[] = [];
  lines.push('# Internal link patches (founder paste vao article tay)');
  lines.push('');
  lines.push(`Generated: ${new Date().toISOString()}`);
  lines.push(`Source: pages/public/internal-link-suggestions.json`);
  lines.push('');
  for (const source of Object.keys(map).sort()) {
    const approved = map[source];
    if (!approved || approved.length === 0) continue;
    const list = artifact.suggestions_by_slug[source] ?? [];
    lines.push(`## Source: \`${source}\``);
    lines.push('');
    for (const target of approved) {
      const s = list.find((x) => x.target_slug === target);
      if (!s) {
        lines.push(`- [ ] Add link to \`${target}\` (no detail found)`);
        continue;
      }
      lines.push(`- [ ] Add anchor: **${s.anchor_text}** -> \`${s.target_slug}\` (method: ${s.method}, conf: ${s.confidence.toFixed(2)})`);
      lines.push(`      Reason: ${s.reason}`);
    }
    lines.push('');
  }
  return lines.join('\n');
}

function TabSuggest() {
  const { data, loading, error, refetch } = useSeoAuditArtifact<SuggestArtifact>('/internal-link-suggestions.json');
  const [decisions, setDecisions] = useState<SuggestDecisionMap>(() => loadSuggestDecisions());
  const [filterCluster, setFilterCluster] = useState<string>('');
  const [filterMethod, setFilterMethod] = useState<string>('');
  const [minConf, setMinConf] = useState<number>(0.4);
  const [orphanOnly, setOrphanOnly] = useState(false);
  const audit = useSeoAuditArtifact<SeoAuditArtifact>('/seo-audit.json');

  if (loading) return <p className="seo-empty">Dang tai goi y lien ket noi bo...</p>;
  if (error) return <ErrorBlock error={error} onRetry={refetch} />;
  if (!data) return <p className="seo-empty">Khong co du lieu goi y.</p>;

  const orphanSet = new Set<string>();
  if (audit.data) {
    for (const a of audit.data.articles) if (a.orphan) orphanSet.add(a.slug);
  }

  const allClusters = new Set<string>();
  for (const list of Object.values(data.suggestions_by_slug)) {
    for (const s of list) allClusters.add(s.target_cluster);
  }

  // Flatten
  type Row = { source: string; suggest: SuggestItem };
  const rows: Row[] = [];
  for (const [source, list] of Object.entries(data.suggestions_by_slug)) {
    for (const s of list) rows.push({ source, suggest: s });
  }
  const filtered = rows.filter((r) => {
    if (filterCluster && r.suggest.target_cluster !== filterCluster) return false;
    if (filterMethod && r.suggest.method !== filterMethod) return false;
    if (r.suggest.confidence < minConf) return false;
    if (orphanOnly && !orphanSet.has(r.suggest.target_slug)) return false;
    return true;
  });

  const totalSuggest = data.summary.total_suggestions;
  let approvedCount = 0;
  let skippedCount = 0;
  const approvedByMethod: Record<string, number> = { tfidf: 0, anchor: 0, sibling: 0 };
  const approvedAnchorTexts: Array<{ anchor: string; conf: number }> = [];
  for (const [source, arr] of Object.entries(decisions)) {
    const sourceList = data.suggestions_by_slug[source] ?? [];
    for (const t of arr) {
      if (t.startsWith('SKIP:')) {
        skippedCount++;
      } else {
        approvedCount++;
        const detail = sourceList.find((x) => x.target_slug === t);
        if (detail) {
          approvedByMethod[detail.method] = (approvedByMethod[detail.method] ?? 0) + 1;
          approvedAnchorTexts.push({ anchor: detail.anchor_text, conf: detail.confidence });
        }
      }
    }
  }
  const pending = totalSuggest - approvedCount - skippedCount;
  const adoptionRate = totalSuggest === 0 ? 0 : (approvedCount / totalSuggest) * 100;
  const topAnchors = approvedAnchorTexts.sort((a, b) => b.conf - a.conf).slice(0, 3);

  function isApproved(source: string, target: string): boolean {
    return (decisions[source] ?? []).includes(target);
  }
  function isSkipped(source: string, target: string): boolean {
    return (decisions[source] ?? []).includes(`SKIP:${target}`);
  }
  function setApprove(source: string, target: string): void {
    setDecisions((prev) => {
      const next = { ...prev };
      const arr = (next[source] ?? []).filter((x) => x !== target && x !== `SKIP:${target}`);
      arr.push(target);
      next[source] = arr;
      saveSuggestDecisions(next);
      return next;
    });
  }
  function setSkip(source: string, target: string): void {
    setDecisions((prev) => {
      const next = { ...prev };
      const arr = (next[source] ?? []).filter((x) => x !== target && x !== `SKIP:${target}`);
      arr.push(`SKIP:${target}`);
      next[source] = arr;
      saveSuggestDecisions(next);
      return next;
    });
  }
  function setReset(source: string, target: string): void {
    setDecisions((prev) => {
      const next = { ...prev };
      const arr = (next[source] ?? []).filter((x) => x !== target && x !== `SKIP:${target}`);
      if (arr.length === 0) delete next[source]; else next[source] = arr;
      saveSuggestDecisions(next);
      return next;
    });
  }

  function handleExport(): void {
    // Strip SKIP markers from decision map for export
    const cleanMap: SuggestDecisionMap = {};
    for (const [src, arr] of Object.entries(decisions)) {
      const approved = arr.filter((x) => !x.startsWith('SKIP:'));
      if (approved.length > 0) cleanMap[src] = approved;
    }
    const md = exportApprovedMarkdown(cleanMap, data as SuggestArtifact);
    const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `internal-link-approved-${new Date().toISOString().slice(0, 10)}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <div className="seo-tab-body">
      <div className="seo-tab-head">
        <p className="seo-tab-meta">
          Tong <strong>{totalSuggest}</strong> goi y | Da duyet <strong>{approvedCount}</strong> | Da bo qua <strong>{skippedCount}</strong> | Con lai <strong>{pending}</strong>
          {' | Sinh ngay '}{data.generated_at}
        </p>
        <button type="button" className="seo-refresh-btn" onClick={() => void refetch()}>
          Lam moi du lieu
        </button>
        <button type="button" className="seo-refresh-btn" onClick={handleExport}>
          Xuat file .md cho cac goi y da duyet
        </button>
      </div>
      <div className="seo-adoption-panel">
        <div className="seo-adoption-card">
          <div className="seo-adoption-label">Tong goi y</div>
          <div className="seo-adoption-value">{totalSuggest}</div>
        </div>
        <div className="seo-adoption-card">
          <div className="seo-adoption-label">Da duyet</div>
          <div className="seo-adoption-value">{approvedCount}</div>
        </div>
        <div className="seo-adoption-card">
          <div className="seo-adoption-label">Adoption rate</div>
          <div className="seo-adoption-value">{adoptionRate.toFixed(1)}%</div>
          <div className="seo-conf-bar"><div className="seo-conf-fill" style={{ width: `${Math.min(100, adoptionRate)}%` }} /></div>
        </div>
        <div className="seo-adoption-card">
          <div className="seo-adoption-label">Method da duyet</div>
          <div className="seo-adoption-method">
            <small>TF-IDF: <strong>{approvedByMethod.tfidf}</strong></small><br />
            <small>Anchor: <strong>{approvedByMethod.anchor}</strong></small><br />
            <small>Sibling: <strong>{approvedByMethod.sibling}</strong></small>
          </div>
        </div>
        <div className="seo-adoption-card seo-adoption-card-wide">
          <div className="seo-adoption-label">Top 3 anchor (do tin cay)</div>
          <ul className="seo-suggest-list">
            {topAnchors.length === 0 ? (
              <li><em>Chua duyet goi y nao.</em></li>
            ) : (
              topAnchors.map((a, i) => (
                <li key={`${a.anchor}__${i}`}><em>{a.anchor}</em> <small>({(a.conf * 100).toFixed(0)}%)</small></li>
              ))
            )}
          </ul>
        </div>
      </div>
      <div className="seo-filter-row">
        <label className="seo-filter-label">
          Cluster dich:
          <select value={filterCluster} onChange={(e) => setFilterCluster(e.target.value)}>
            <option value="">Tat ca</option>
            {Array.from(allClusters).sort().map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </label>
        <label className="seo-filter-label">
          Phuong phap:
          <select value={filterMethod} onChange={(e) => setFilterMethod(e.target.value)}>
            <option value="">Tat ca</option>
            <option value="tfidf">TF-IDF</option>
            <option value="anchor">Anchor</option>
            <option value="sibling">Sibling</option>
          </select>
        </label>
        <label className="seo-filter-label">
          Do tin cay toi thieu: {minConf.toFixed(2)}
          <input type="range" min={0.4} max={0.95} step={0.05} value={minConf}
            onChange={(e) => setMinConf(parseFloat(e.target.value))} />
        </label>
        <label className="seo-checkbox-label">
          <input type="checkbox" checked={orphanOnly} onChange={(e) => setOrphanOnly(e.target.checked)} />
          Chi hien bai dich la bai co don
        </label>
      </div>
      <p className="seo-note">
        Hien {filtered.length} goi y sau khi loc. Action chi luu trong trinh duyet (localStorage) - khong ghi server.
        Nut "Xuat .md" cho phep founder tai ve file patch de copy vao article tay.
      </p>
      <table className="seo-table">
        <thead>
          <tr>
            <th>Bai nguon</th>
            <th>Bai dich</th>
            <th>Anchor text de xuat</th>
            <th>Method</th>
            <th>Do tin cay</th>
            <th>Hanh dong</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((r, i) => {
            const approved = isApproved(r.source, r.suggest.target_slug);
            const skipped = isSkipped(r.source, r.suggest.target_slug);
            const rowClass = approved ? 'seo-row-ok' : skipped ? 'seo-row-warn' : '';
            return (
              <tr key={`${r.source}__${r.suggest.target_slug}__${i}`} className={rowClass}>
                <td><code>{r.source}</code></td>
                <td>
                  <a href={`/blog/${r.suggest.target_slug.replace(/^\/blog\//, '').replace(/^\/en\/[^/]+\//, '')}`} target="_blank" rel="noreferrer">{r.suggest.target_title}</a>
                  <br /><small><code>{r.suggest.target_slug}</code> | cluster <code>{r.suggest.target_cluster}</code></small>
                </td>
                <td><em>{r.suggest.anchor_text}</em><br /><small>{r.suggest.reason}</small></td>
                <td>
                  <span className={`seo-badge seo-badge-${r.suggest.method === 'anchor' ? 'ok' : r.suggest.method === 'tfidf' ? 'warn' : 'idle'}`}>
                    {r.suggest.method}
                  </span>
                </td>
                <td>
                  <div className="seo-conf-bar">
                    <div className="seo-conf-fill" style={{ width: `${Math.round(r.suggest.confidence * 100)}%` }} />
                  </div>
                  <small>{(r.suggest.confidence * 100).toFixed(1)}%</small>
                </td>
                <td>
                  {approved ? (
                    <button type="button" className="seo-action-link" onClick={() => setReset(r.source, r.suggest.target_slug)}>Da duyet (huy)</button>
                  ) : skipped ? (
                    <button type="button" className="seo-action-link" onClick={() => setReset(r.source, r.suggest.target_slug)}>Da bo (huy)</button>
                  ) : (
                    <>
                      <button type="button" className="seo-action-link" onClick={() => setApprove(r.source, r.suggest.target_slug)}>Duyet</button>
                      {' | '}
                      <button type="button" className="seo-action-link" onClick={() => setSkip(r.source, r.suggest.target_slug)}>Bo qua</button>
                    </>
                  )}
                </td>
              </tr>
            );
          })}
          {filtered.length === 0 ? (
            <tr><td colSpan={6} className="seo-empty">Khong co goi y nao khop bo loc.</td></tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

// ---------- Tab: Lưu lượng truy cập (GA4 Ship B 2026-05-12) ----------

interface Ga4Totals {
  sessions: number;
  total_users: number;
  page_views: number;
  avg_session_duration: number;
  bounce_rate: number;
}

interface Ga4TopPage {
  page_path: string;
  views: number;
  users: number;
  avg_duration: number;
  bounce_rate: number;
  blog_slug?: string;
}

interface Ga4TopSource {
  source: string;
  medium: string;
  sessions: number;
  share_pct: number;
  tag: 'organic' | 'direct' | 'social' | 'referral' | 'other';
}

interface Ga4TrendPoint {
  date: string;
  sessions: number;
  users: number;
}

interface Ga4OverviewResp {
  ok: boolean;
  mode?: string;
  error?: string;
  property_id?: string;
  window?: { start: string; end: string; days: number };
  previous_window?: { start: string; end: string; days: number };
  totals?: Ga4Totals;
  previous_totals?: Ga4Totals;
  trend_28d?: Ga4TrendPoint[];
  top_pages?: Ga4TopPage[];
  top_sources?: Ga4TopSource[];
  generated_at?: string;
}

interface Ga4RealtimeResp {
  ok: boolean;
  mode?: string;
  error?: string;
  property_id?: string;
  active_users?: number;
  top_pages_now?: Array<{ screen: string; active_users: number }>;
  generated_at?: string;
}

function fmtDuration(sec: number): string {
  if (!sec || !Number.isFinite(sec)) return '0s';
  if (sec < 60) return `${sec.toFixed(1)}s`;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec - m * 60);
  return `${m}p ${s}s`;
}

function fmtBounce(v: number): string {
  if (!Number.isFinite(v)) return '-';
  return `${(v * 100).toFixed(1)}%`;
}

function sourceTagLabel(tag: Ga4TopSource['tag']): string {
  switch (tag) {
    case 'organic':
      return 'Tu nhien';
    case 'direct':
      return 'Truc tiep';
    case 'social':
      return 'Mang xa hoi';
    case 'referral':
      return 'Gioi thieu';
    default:
      return 'Khac';
  }
}

function sourceTagClass(tag: Ga4TopSource['tag']): string {
  switch (tag) {
    case 'organic':
      return 'seo-badge seo-badge-ok';
    case 'direct':
      return 'seo-badge seo-badge-idle';
    case 'social':
      return 'seo-badge seo-badge-warn';
    case 'referral':
      return 'seo-badge seo-badge-idle';
    default:
      return 'seo-badge seo-badge-idle';
  }
}

function Ga4EmptyState({ data, refetch }: { data: Ga4OverviewResp; refetch: () => void }) {
  const mode = data.mode ?? 'unknown';
  if (mode === 'no_credentials') {
    return (
      <div className="seo-empty-state">
        <p>GA4 credential chua duoc cau hinh tren production. Founder grant 3 secret qua wrangler:</p>
        <ul className="seo-suggest-list">
          <li><code>GA4_SERVICE_ACCOUNT_JSON</code></li>
          <li><code>GA4_PROPERTY_ID</code></li>
          <li><code>GA4_MEASUREMENT_ID</code></li>
        </ul>
      </div>
    );
  }
  if (mode === 'ga4_permission_denied') {
    return (
      <div className="seo-empty-state">
        <p>Service account chua co quyen Viewer tren GA4 Property.</p>
        <p className="seo-note">
          Founder mo <a href="https://analytics.google.com" target="_blank" rel="noreferrer">GA4 Admin</a> -&gt; Property Access Management
          -&gt; Add user voi email <code>asklaw-ga4-reader@dark-safeguard-495317-c2.iam.gserviceaccount.com</code> (Role: Viewer).
        </p>
        <button type="button" className="seo-refresh-btn" onClick={refetch}>Thu lai sau khi cap quyen</button>
      </div>
    );
  }
  if (mode === 'ga4_auth_failed') {
    return <ErrorBlock error={data.error ?? 'Xac thuc GA4 that bai. Kiem tra private_key co dung JSON va chua bi revoke.'} onRetry={refetch} />;
  }
  if (mode === 'ga4_rate_limited') {
    return <ErrorBlock error={data.error ?? 'GA4 rate limited - thu lai sau vai phut.'} onRetry={refetch} />;
  }
  return <ErrorBlock error={data.error ?? 'Khong tai duoc du lieu GA4.'} onRetry={refetch} />;
}

function TabTraffic() {
  const overview = useAdminSeoApi<Ga4OverviewResp>('/ga4/overview');
  const realtime = useAdminSeoApi<Ga4RealtimeResp>('/ga4/realtime');

  // Realtime auto-refetch every 60s
  useEffect(() => {
    const id = window.setInterval(() => {
      void realtime.refetch();
    }, 60_000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (overview.loading) return <p className="seo-empty">Dang tai du lieu Google Analytics 4...</p>;
  if (overview.error) return <ErrorBlock error={overview.error} onRetry={overview.refetch} />;
  if (!overview.data) return <p className="seo-empty">Khong co du lieu.</p>;
  if (!overview.data.ok) return <Ga4EmptyState data={overview.data} refetch={overview.refetch} />;

  const totals = overview.data.totals ?? { sessions: 0, total_users: 0, page_views: 0, avg_session_duration: 0, bounce_rate: 0 };
  const prev = overview.data.previous_totals ?? { sessions: 0, total_users: 0, page_views: 0, avg_session_duration: 0, bounce_rate: 0 };
  const trend = overview.data.trend_28d ?? [];
  const topPages = overview.data.top_pages ?? [];
  const topSources = overview.data.top_sources ?? [];
  const isEmpty = totals.sessions === 0 && totals.page_views === 0;

  return (
    <div className="seo-tab-body">
      <div className="seo-tab-head">
        <p className="seo-tab-meta">
          Property: <code>{overview.data.property_id}</code> | Cua so {overview.data.window?.days ?? 28} ngay
          {' | Cập nhật '}{fmtTs(overview.data.generated_at)}
        </p>
        <button type="button" className="seo-refresh-btn" onClick={() => { void overview.refetch(); void realtime.refetch(); }}>
          Lam moi du lieu
        </button>
      </div>

      {isEmpty ? (
        <p className="seo-empty">
          GA4 property co the chua nhan du lieu (moi tao 24-48 gio). Quay lai sau khi du lieu dau tien dat ve.
          Banner nay tu dong an khi co luot phien dau tien.
        </p>
      ) : null}

      <div className="seo-totals-grid">
        <div className="seo-total-card">
          <div className="seo-total-label">Phien (Sessions)</div>
          <div className="seo-total-value">{totals.sessions.toLocaleString('vi-VN')}</div>
          <div className="seo-total-trend"><TrendBadge curr={totals.sessions} prev={prev.sessions} /> so voi 28 ngay truoc</div>
        </div>
        <div className="seo-total-card">
          <div className="seo-total-label">Nguoi dung (Users)</div>
          <div className="seo-total-value">{totals.total_users.toLocaleString('vi-VN')}</div>
          <div className="seo-total-trend"><TrendBadge curr={totals.total_users} prev={prev.total_users} /> so voi 28 ngay truoc</div>
        </div>
        <div className="seo-total-card">
          <div className="seo-total-label">Luot xem trang</div>
          <div className="seo-total-value">{totals.page_views.toLocaleString('vi-VN')}</div>
          <div className="seo-total-trend"><TrendBadge curr={totals.page_views} prev={prev.page_views} /> so voi 28 ngay truoc</div>
        </div>
        <div className="seo-total-card">
          <div className="seo-total-label">TG phien TB</div>
          <div className="seo-total-value">{fmtDuration(totals.avg_session_duration)}</div>
          <div className="seo-total-trend"><TrendBadge curr={totals.avg_session_duration} prev={prev.avg_session_duration} /> so voi 28 ngay truoc</div>
        </div>
        <div className="seo-total-card">
          <div className="seo-total-label">Bounce rate</div>
          <div className="seo-total-value">{fmtBounce(totals.bounce_rate)}</div>
          <div className="seo-total-trend"><TrendBadge curr={totals.bounce_rate} prev={prev.bounce_rate} invert /> so voi 28 ngay truoc</div>
        </div>
      </div>

      <div className="seo-card">
        <h3 className="seo-card-title">Hiện tại (cập nhật mỗi 60 giây)</h3>
        {realtime.loading && !realtime.data ? (
          <p className="seo-empty">Dang tai du lieu realtime...</p>
        ) : realtime.data && realtime.data.ok ? (
          <>
            <p>
              Nguoi dung dang truy cap: <strong className="seo-total-value" style={{ fontSize: '1.5em' }}>{(realtime.data.active_users ?? 0).toLocaleString('vi-VN')}</strong>
            </p>
            {(realtime.data.top_pages_now ?? []).length > 0 ? (
              <ul className="seo-suggest-list">
                {(realtime.data.top_pages_now ?? []).map((p, i) => (
                  <li key={`${p.screen}__${i}`}>{p.screen || '(khong xac dinh)'} - {p.active_users} nguoi</li>
                ))}
              </ul>
            ) : (
              <p className="seo-note">Chua co trang nao dang co nguoi xem.</p>
            )}
          </>
        ) : (
          <p className="seo-note">{realtime.data?.error ?? 'Khong tai duoc realtime.'}</p>
        )}
      </div>

      {trend.length > 0 ? (
        <div className="seo-card">
          <h3 className="seo-card-title">Xu huong 28 ngay (phien va nguoi dung)</h3>
          <p className="seo-note">Tong {trend.length} diem du lieu. Diem moi nhat: {trend[trend.length - 1]?.date} = {trend[trend.length - 1]?.sessions} phien.</p>
        </div>
      ) : null}

      <h3 className="seo-card-title">Top 50 trang (sap xep theo luot xem)</h3>
      <table className="seo-table">
        <thead>
          <tr>
            <th>Duong dan</th>
            <th>Luot xem</th>
            <th>Nguoi dung</th>
            <th>TG TB</th>
            <th>Bounce</th>
            <th>Lien ket</th>
          </tr>
        </thead>
        <tbody>
          {topPages.map((p) => (
            <tr key={p.page_path}>
              <td><code>{p.page_path}</code></td>
              <td>{p.views.toLocaleString('vi-VN')}</td>
              <td>{p.users.toLocaleString('vi-VN')}</td>
              <td>{fmtDuration(p.avg_duration)}</td>
              <td>{fmtBounce(p.bounce_rate)}</td>
              <td>
                {p.blog_slug ? (
                  <a href={`/blog/${p.blog_slug}`} target="_blank" rel="noreferrer">Mo blog</a>
                ) : (
                  <a href={p.page_path} target="_blank" rel="noreferrer">Mo trang</a>
                )}
              </td>
            </tr>
          ))}
          {topPages.length === 0 ? (
            <tr><td colSpan={6} className="seo-empty">Chua co du lieu trang.</td></tr>
          ) : null}
        </tbody>
      </table>

      <h3 className="seo-card-title">Top 20 nguon truy cap (theo phien)</h3>
      <table className="seo-table">
        <thead>
          <tr>
            <th>Nguon</th>
            <th>Phuong tien</th>
            <th>Phien</th>
            <th>% chia se</th>
            <th>Loai</th>
          </tr>
        </thead>
        <tbody>
          {topSources.map((s, i) => (
            <tr key={`${s.source}__${s.medium}__${i}`}>
              <td>{s.source || '(direct)'}</td>
              <td>{s.medium || '(none)'}</td>
              <td>{s.sessions.toLocaleString('vi-VN')}</td>
              <td>{s.share_pct}%</td>
              <td><span className={sourceTagClass(s.tag)}>{sourceTagLabel(s.tag)}</span></td>
            </tr>
          ))}
          {topSources.length === 0 ? (
            <tr><td colSpan={5} className="seo-empty">Chua co du lieu nguon.</td></tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

// ---------- Tab: Daily Brief ----------

/**
 * MD-25 Phase 3 Daily Brief tab.
 *
 * Endpoint admin `/api/v1/admin/seo/daily-brief?date=YYYY-MM-DD` tra ve full HTML
 * (4 phan A/B/C/D + autofix log + hang doi thu cong). Vi response co full
 * `<html><head><style>...` standalone, dung iframe + blob URL de cach ly style
 * voi SPA Tailwind. JWT Bearer header gan qua fetch (iframe src= attribute khong
 * truyen header truc tiep).
 */
function TabDailyBrief() {
  const defaultDate = (() => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
  })();

  const [date, setDate] = useState<string>(defaultDate);
  const [iframeSrc, setIframeSrc] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [reloadTick, setReloadTick] = useState<number>(0);

  useEffect(() => {
    let revoked = false;
    let currentBlobUrl = '';

    const fetchBrief = async () => {
      setLoading(true);
      setError('');
      setIframeSrc('');
      let token = '';
      try {
        token = localStorage.getItem('asklaw_access_token') ?? '';
      } catch {
        token = '';
      }
      if (!token) {
        setError('Không tìm thấy mã đăng nhập (JWT). Vui lòng đăng nhập lại tài khoản quản trị.');
        setLoading(false);
        return;
      }
      try {
        const resp = await fetch(
          `/api/v1/admin/seo/daily-brief?date=${encodeURIComponent(date)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!resp.ok) {
          const body = await resp.text().catch(() => '');
          setError(`HTTP ${resp.status}: ${body.slice(0, 200) || 'Máy chủ trả lỗi'}`);
          setLoading(false);
          return;
        }
        const html = await resp.text();
        if (revoked) return;
        const blob = new Blob([html], { type: 'text/html; charset=utf-8' });
        currentBlobUrl = URL.createObjectURL(blob);
        setIframeSrc(currentBlobUrl);
      } catch (e) {
        if (!revoked) {
          setError(e instanceof Error ? e.message : 'Lỗi không xác định khi tải bản tin');
        }
      } finally {
        if (!revoked) setLoading(false);
      }
    };

    void fetchBrief();

    return () => {
      revoked = true;
      if (currentBlobUrl) {
        URL.revokeObjectURL(currentBlobUrl);
      }
    };
  }, [date, reloadTick]);

  const handleReload = () => {
    setReloadTick((k) => k + 1);
  };

  return (
    <div className="seo-daily-brief-tab">
      <div
        className="seo-daily-brief-toolbar"
        style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}
      >
        <label htmlFor="seo-daily-brief-date" style={{ fontSize: '14px', fontWeight: 500 }}>
          Ngày:
        </label>
        <input
          id="seo-daily-brief-date"
          type="date"
          value={date}
          max={defaultDate}
          onChange={(e) => setDate(e.target.value)}
          style={{
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            padding: '4px 8px',
            fontSize: '14px',
          }}
        />
        <button
          type="button"
          className="seo-refresh-btn"
          onClick={handleReload}
          disabled={loading}
        >
          {loading ? 'Đang tải...' : 'Tải lại'}
        </button>
        <span style={{ fontSize: '12px', color: '#6b7280' }}>
          Bản tin được tạo bởi cron SEO hằng ngày; nếu ngày hôm nay chưa có dữ liệu, hãy chọn ngày hôm trước.
        </span>
      </div>

      {error ? (
        <div
          style={{
            padding: '12px 16px',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            color: '#991b1b',
            fontSize: '14px',
            borderRadius: '6px',
            marginBottom: '12px',
          }}
        >
          Lỗi: {error}
        </div>
      ) : null}

      {iframeSrc ? (
        <iframe
          key={iframeSrc}
          src={iframeSrc}
          title="Bản tin SEO hằng ngày"
          style={{
            width: '100%',
            height: '80vh',
            minHeight: '600px',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            background: '#fff',
          }}
          sandbox="allow-same-origin"
        />
      ) : !error && loading ? (
        <p style={{ fontSize: '14px', color: '#6b7280' }}>Đang tải bản tin từ máy chủ...</p>
      ) : null}
    </div>
  );
}

// ---------- Shared ----------

function ErrorBlock({ error, onRetry }: { error: string; onRetry: () => void }) {
  return (
    <div className="seo-error-block">
      <p className="seo-error">Loi: {error}</p>
      <button type="button" className="seo-refresh-btn" onClick={onRetry}>Thu lai</button>
    </div>
  );
}

// ---------- Container ----------

function SeoInner() {
  const [tab, setTab] = useState<TabId>('cron');
  const active = TABS.find((t) => t.id === tab) ?? TABS[0];
  return (
    <main className="dashboard-shell seo-dashboard">
      <header className="seo-header">
        <h1 className="seo-title">SEO Bootstrap</h1>
        <p className="seo-sub">
          Bang dieu khien theo doi cong viec SEO Phase 0: 4 cron tu dong + on-page audit
          (sitemap, schema, lien ket noi bo). Du lieu chi doc, khong ghi co so du lieu.
        </p>
        <p className="seo-phase2-banner">
          Phase 1 LIVE (cron + on-page audit). Phase 2 Ship A LIVE (GSC Search Analytics).
          Phase 2 Ship B LIVE (GA4 Data API - dang doi 24-48h propagate service account + thu thap du lieu).
        </p>
      </header>

      <nav className="seo-tabs" role="tablist" aria-label="Cac tab SEO Bootstrap">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={`seo-tab-btn ${tab === t.id ? 'seo-tab-btn-active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <section className="seo-tab-section">
        <h2 className="seo-tab-title">{active.label}</h2>
        <p className="seo-tab-sub">{active.sub}</p>
        {tab === 'cron' ? <TabCron /> : null}
        {tab === 'gsc' ? <TabGsc /> : null}
        {tab === 'perf' ? <TabPerf /> : null}
        {tab === 'daily-brief' ? <TabDailyBrief /> : null}
        {tab === 'sitemap' ? <TabSitemap /> : null}
        {tab === 'schema' ? <TabSchema /> : null}
        {tab === 'link' ? <TabLink /> : null}
        {tab === 'suggest' ? <TabSuggest /> : null}
        {tab === 'traffic' ? <TabTraffic /> : null}
      </section>

      <footer className="seo-footer">
        <p>
          Phien ban Phase 0 - chi danh cho founder va admin. Du lieu noi bo, khong xuat
          ra nguoi dung cuoi.
        </p>
      </footer>
    </main>
  );
}

export function DashboardSeoRoute() {
  return (
    <DashboardGate>
      <DashboardLayout>
        <SeoInner />
      </DashboardLayout>
    </DashboardGate>
  );
}

// Make sure consumers can hoa pho ageDays even when not invoking the tabs.
export { ageDays };
